// Auth + backend sync context for Tempo leggero
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { getState, hydrateFromRemote, applyServerCategories } from "@/lib/store";
import { enqueueTaskUpsert, enqueueTaskDelete, enqueueMeta, enqueueCategoryDelete, setCategoriesMergedHandler, setServerReachable, drainNow, setRemoteChangeHandler, getPendingOps } from "@/lib/sync";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Don't let a server snapshot bring back a category whose delete is still queued
function withoutPendingDeletes(remote) {
  const ids = new Set(getPendingOps().filter((o) => o.type === "category_delete").map((o) => o.id));
  if (!ids.size || !Array.isArray(remote?.categories)) return remote;
  return { ...remote, categories: remote.categories.filter((c) => !ids.has(c.id)) };
}

const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {}, requestMagicLink: async () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      const status = err?.response?.status;
      // Only clear user on a true unauthenticated response.
      // On network errors / 5xx / timeouts, keep current user to avoid spurious logouts
      // and preserve the local sync queue for retry.
      if (status === 401 || status === 403) {
        setUser(null);
      } else {
        console.error("auth /me transient error, keeping session:", status || err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google sign-in: GoogleCallback exchanges the session_id first, then calls refresh()
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  // Pull remote planner + push local changes when user is authenticated
  useEffect(() => {
    let unsub = () => {};
    let prevTaskMap = new Map();
    let cancelled = false;
    let retryTimer = null;
    async function bootstrapSync() {
      if (!user) return;
      // 1) Drain any queued ops from previous sessions FIRST, so the server
      //    has our latest local edits before we read the authoritative snapshot.
      try { await drainNow(); } catch (err) { console.error("initial drain failed:", err); }

      let remote = null;
      let fetchOk = false;
      const startCatIds = new Set(getState().categories.map((c) => c.id));
      let addedCats = [];
      try {
        const { data } = await api.get("/planner");
        remote = data && data.data ? data.data : null;
        // Keep categories created while the planner was loading (they aren't on the server yet)
        addedCats = getState().categories.filter((c) => !startCatIds.has(c.id));
        const added = addedCats;
        if (remote && added.length && Array.isArray(remote.categories) && remote.categories.length) {
          const ids = new Set(remote.categories.map((c) => c.id));
          remote = { ...remote, categories: [...remote.categories, ...added.filter((c) => !ids.has(c.id))] };
        }
        fetchOk = true;
        setServerReachable(true);
      } catch (err) {
        if (!err?.response || err.response.status >= 500) setServerReachable(false);
        // Transient network/server failure: DO NOT clobber local state or queue.
        console.error("planner initial fetch failed, keeping local state:", err?.response?.status || err?.message);
      }

      if (remote) {
        // 2) Reconcile: any ops STILL pending in the queue (because the network is
        //    still down or the request just enqueued) must win over the server snapshot.
        //    We overlay pending upserts and drop pending deletes on top of the remote.
        try {
          const pending = getPendingOps();
          const pendingUpserts = new Map();
          const pendingDeletes = new Set();
          for (const op of pending) {
            if (op.type === "task_upsert") pendingUpserts.set(op.id, op.task);
            else if (op.type === "task_delete") { pendingDeletes.add(op.id); pendingUpserts.delete(op.id); }
          }
          const remoteTasks = Array.isArray(remote.tasks) ? remote.tasks : [];
          const merged = [];
          const seen = new Set();
          for (const t of remoteTasks) {
            if (!t || !t.id) continue;
            if (pendingDeletes.has(t.id)) continue;
            if (pendingUpserts.has(t.id)) {
              merged.push(pendingUpserts.get(t.id));
              seen.add(t.id);
            } else {
              merged.push(t);
              seen.add(t.id);
            }
          }
          // Include local-only pending upserts not yet reflected on the server
          for (const [id, task] of pendingUpserts) {
            if (!seen.has(id)) merged.push(task);
          }
          hydrateFromRemote(withoutPendingDeletes({ ...remote, tasks: merged }));
        } catch (err) {
          console.error("merge failed, falling back to remote:", err);
          hydrateFromRemote(withoutPendingDeletes(remote));
        }
        if (addedCats.length) {
          const local = getState();
          enqueueMeta({ tasks: local.tasks, categories: local.categories, settings: local.settings, journal: local.journal });
        }
      } else if (fetchOk) {
        // First login (nothing on the server yet): push local state up as full snapshot
        const local = getState();
        enqueueMeta({
          tasks: local.tasks, categories: local.categories,
          settings: local.settings, journal: local.journal
        });
      }
      // Track task changes → per-task upserts / deletes
      const initState = getState();
      prevTaskMap = new Map((initState.tasks || []).map((t) => [t.id, t]));
      unsub = subscribeStoreChanges(() => {
        const s = getState();
        const cur = new Map((s.tasks || []).map((t) => [t.id, t]));
        // Diff by id
        for (const [id, task] of cur) {
          const prev = prevTaskMap.get(id);
          if (!prev || JSON.stringify(prev) !== JSON.stringify(task)) {
            enqueueTaskUpsert(task);
          }
        }
        for (const [id] of prevTaskMap) {
          if (!cur.has(id)) enqueueTaskDelete(id);
        }
        prevTaskMap = cur;
        // Debounced meta save (settings/categories/journal + full tasks snapshot as safety net)
        enqueueMeta({
          tasks: s.tasks, categories: s.categories,
          settings: s.settings, journal: s.journal
        });
      });
      // Multi-tab: refetch on remote change from another tab
      const onCatDeleted = (e) => enqueueCategoryDelete(e.detail?.id);
      window.addEventListener("tl:category-deleted", onCatDeleted);
      setCategoriesMergedHandler(applyServerCategories);
      const prevUnsub = unsub;
      unsub = () => { prevUnsub(); window.removeEventListener("tl:category-deleted", onCatDeleted); setCategoriesMergedHandler(null); };
      setRemoteChangeHandler(async () => {
        try {
          const { data } = await api.get("/planner");
          if (data && data.data) {
            hydrateFromRemote(withoutPendingDeletes(data.data));
            prevTaskMap = new Map((getState().tasks || []).map((t) => [t.id, t]));
          }
        } catch {}
      });
      // First load failed: keep retrying quietly so the real categories/tasks replace local defaults
      if (!fetchOk) {
        let retries = 0;
        const retry = async () => {
          if (cancelled) return;
          try {
            await drainNow();
            const { data } = await api.get("/planner");
            if (cancelled) return;
            setServerReachable(true);
            if (data && data.data) {
              hydrateFromRemote(withoutPendingDeletes(data.data));
              prevTaskMap = new Map((getState().tasks || []).map((t) => [t.id, t]));
            }
          } catch {
            if (++retries < 20) retryTimer = setTimeout(retry, 5000);
          }
        };
        retryTimer = setTimeout(retry, 5000);
      }
    }
    bootstrapSync();
    return () => { cancelled = true; clearTimeout(retryTimer); unsub(); setRemoteChangeHandler(null); };
  }, [user]);

  // Passwordless login: ask the backend to email a single-use access link
  const requestMagicLink = useCallback(async (email, stripeSessionId) => {
    const body = {};
    if (email) body.email = String(email).trim().toLowerCase();
    if (stripeSessionId) body.stripe_session_id = stripeSessionId;
    const { data } = await api.post("/auth/magic-link", body);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("logout failed:", err); }
    setUser(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, requestMagicLink, logout, api }),
    [user, loading, refresh, requestMagicLink, logout]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }

// Helper to subscribe to store changes without importing internal listeners
function subscribeStoreChanges(cb) {
  const handler = () => cb();
  window.addEventListener("tl:store-changed", handler);
  return () => window.removeEventListener("tl:store-changed", handler);
}
